import axios from "axios";
import dayjs from "dayjs";

axios.defaults.withCredentials = true;

let refreshPromise = null;

const TIMEOUT_MS = 30000;
const TOKEN_EXPIRY_THRESHOLD_SECONDS = 30;

const handleTokenExpiry = (tokenExpiry) => {
    if (!tokenExpiry) return false;
    const isTokenExpiringSoon =
        dayjs.unix(tokenExpiry).diff(dayjs(), "second") < TOKEN_EXPIRY_THRESHOLD_SECONDS;
    return isTokenExpiringSoon;
};

const checkSession = async () => {
    try {
        const response = await axios.get(
            `${process.env.REACT_APP_API_URL_ACCOUNTS}api/v2/users/session`,
            { timeout: 10000 }
        );
        return response.data.isAuthenticated;
    } catch (error) {
        if (error?.response?.status === 503) {
            window.location.replace("/maintenance");
            throw error; // Prevent further execution
        }
        // Log other errors but don't throw
        console.error("Session check failed:", error);
        return false;
    }
};

const clearSessionAndRedirect = () => {
    // Store session state before clearing
    const sessionData = { session: false };
    window.localStorage.clear();
    localStorage.setItem("general", JSON.stringify(sessionData));
    window.location.replace("/join");
};

const refreshToken = async (user_data) => {
    try {
        const csrfResponse = await axios.get(
            `${process.env.REACT_APP_API_URL_ACCOUNTS}api/v2/users/csrf`,
            {
                timeout: 10000,
                withCredentials: true
            }
        );

        if (csrfResponse.status !== 200) {
            throw new Error("Failed to get CSRF token");
        }

        const csrfToken = csrfResponse.headers["x-csrftoken"];

        const tokenRefreshResponse = await axios.post(
            `${process.env.REACT_APP_API_URL_ACCOUNTS}api/v2/users/token-refresh`,
            {},
            {
                timeout: 10000,
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                withCredentials: true
            }
        );

        const updated_user_data = {
            firstName: user_data?.firstName,
            lastName: user_data?.lastName,
            isSignup: user_data?.isSignup,
            userId: user_data?.userId,
            isSubscribed: user_data?.isSubscribed,
            profilePicture: user_data?.profilePicture,
            subscriptionExpiry: user_data?.subscriptionExpiry,
            accessToken: tokenRefreshResponse?.data?.access_token,
            tokenExpiry: tokenRefreshResponse?.data?.expire_at,
        };

        localStorage.setItem("user_data", JSON.stringify(updated_user_data));

        return tokenRefreshResponse?.data?.access_token;
    } catch (error) {
        if (error?.response?.status === 403) {
            clearSessionAndRedirect();
        } else if (error?.response?.status === 503) {
            window.location.replace("/maintenance");
        }
        throw error;
    }
};

// Centralized refresh logic
const performTokenRefresh = async (user_data) => {
    // Use existing promise if refresh is already in progress
    if (refreshPromise) {
        return refreshPromise;
    }

    // Check session before attempting refresh
    const isSessionActive = await checkSession();
    if (!isSessionActive) {
        clearSessionAndRedirect();
        throw new Error("Session expired");
    }

    // Start new refresh
    refreshPromise = refreshToken(user_data)
        .finally(() => {
            refreshPromise = null;
        });

    return refreshPromise;
};

const axiosInstance = (url) => {
    const instance = axios.create({
        baseURL: url,
        timeout: TIMEOUT_MS,
    });

    instance.interceptors.request.use(async (config) => {
        // Parse user data
        let user_data = null;
        try {
            const userData = localStorage.getItem("user_data");
            user_data = userData ? JSON.parse(userData) : null;
        } catch (error) {
            console.error("Failed to parse user data:", error);
        }

        const accessToken = user_data?.accessToken;
        const tokenExpiry = user_data?.tokenExpiry;

        // If token is expiring soon, refresh it
        if (accessToken && handleTokenExpiry(tokenExpiry)) {
            try {
                const newToken = await performTokenRefresh(user_data);
                config.headers.Authorization = `Bearer ${newToken}`;
                return config;
            } catch (error) {
                return Promise.reject(error);
            }
        }

        // Otherwise use existing token
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }

        return config;
    }, (error) => {
        return Promise.reject(error);
    });

    instance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            // Prevent infinite retry loop
            if (originalRequest?._retry) {
                return Promise.reject(error);
            }

            if (error?.response?.status === 401) {
                originalRequest._retry = true;

                // Parse user data
                let user_data = null;
                try {
                    const userData = localStorage.getItem("user_data");
                    user_data = userData ? JSON.parse(userData) : null;
                } catch (parseError) {
                    console.error("Failed to parse user data:", parseError);
                }

                if (!user_data) {
                    clearSessionAndRedirect();
                    return Promise.reject(error);
                }

                try {
                    const newToken = await performTokenRefresh(user_data);
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return instance(originalRequest);
                } catch (refreshError) {
                    return Promise.reject(refreshError);
                }
            } else if (error?.response?.status === 403) {
                clearSessionAndRedirect();
            } else if (error?.response?.status === 503) {
                window.location.replace("/maintenance");
            }

            return Promise.reject(error);
        }
    );

    return instance;
};

export const Payments = axiosInstance(process.env.REACT_APP_API_URL_PAYMENTS);
export const Activity = axiosInstance(process.env.REACT_APP_API_URL_ACTIVITY);
export const Elearn = axiosInstance(process.env.REACT_APP_API_URL_ELEARN);
export const Accounts = axiosInstance(process.env.REACT_APP_API_URL_ACCOUNTS);